import {agentsApi, messagesService, sessionService} from '@dv5/api'
import type {RemotePart, RemotePartMutation} from '@dv5/api'
import {
    remotePartsLoading, remotePartsReceived, remotePartsLoadFailed, remotePartsMutationStarted,
    remotePartsMutationFinished, remotePartsDisconnected, remotePartsForgotten, remotePartsCleared,
} from '@dv5/domain'
import {store} from '@dv5/domain/store'
import type {RemotePartsSnapshot} from '@dv5/domain'

type Action = 'subscribe' | 'get' | 'create' | 'update'

export interface RemotePartsResult extends RemotePartsSnapshot {
    requestId: string;
    action: Action
}

export interface RemotePartsError {
    documentId: string;
    requestId: string;
    action: string;
    code: string;
    message: string;
    outcome: 'unknown' | 'rejected'
}

export class RemotePartsFailure extends Error {
    constructor(message: string, public readonly outcome: 'unknown' | 'rejected', public readonly code: string) {
        super(message)
    }
}

interface Pending {
    documentId: string
    action: Action
    fingerprint: string
    promise: Promise<RemotePart[]>
    resolve: (parts: RemotePart[]) => void
    reject: (error: RemotePartsFailure) => void
    timer: ReturnType<typeof setTimeout>
}

const isLoad = (action: Action) => action === 'subscribe' || action === 'get'

class RemoteDocumentPartsService {
    private connected = false
    private subscriptions = new Map<string, number>()
    private requests = new Map<string, Pending>()
    private registered = false
    private recoveryLoads = new Set<string>()

    register() {
        if (this.registered) return
        this.registered = true
        sessionService.onClear(() => this.clear())
        agentsApi.onRemoved(({changes}) => this.forget(changes))
        // Presence recovery uses WS loads; it never replays a mutation.
        agentsApi.subscribe(changedDocument => {
            if (!this.connected) return
            if (changedDocument) {
                if (this.subscriptions.has(changedDocument)) this.reload(changedDocument)
            } else {
                for (const documentId of this.subscriptions.keys()) {
                    if (this.recoveryLoads.has(documentId)) this.reload(documentId)
                }
            }
        })
    }

    setConnected(connected: boolean) {
        if (connected === this.connected) return
        this.connected = connected
        if (connected) {
            for (const documentId of this.subscriptions.keys()) this.reload(documentId)
        } else {
            for (const [requestId, request] of [...this.requests]) this.fail({
                documentId: request.documentId,
                requestId,
                action: request.action,
                code: 'OUTCOME_UNKNOWN',
                outcome: 'unknown',
                message: 'Connection lost before confirmation. Reconnect and retry the same request. Your draft is retained.',
            })
            store.dispatch(remotePartsDisconnected())
        }
    }

    subscribe(documentId: string) {
        this.register()
        const count = this.subscriptions.get(documentId) || 0
        this.subscriptions.set(documentId, count + 1)
        if (count === 0 && this.connected) this.reload(documentId)
        return () => {
            const remaining = (this.subscriptions.get(documentId) || 0) - 1
            if (remaining > 0) this.subscriptions.set(documentId, remaining)
            else this.forget(documentId)
        }
    }

    reload(documentId: string) {
        if (!this.connected) return
        // Coalesce lifecycle invalidations while a load is already pending.
        if ([...this.requests.values()].some(request => request.documentId === documentId && isLoad(request.action))) return
        void this.request(documentId, 'subscribe', {requestId: crypto.randomUUID()}).catch(() => {
        })
    }

    create(documentId: string, mutation: RemotePartMutation) {
        return this.request(documentId, 'create', mutation)
    }

    update(documentId: string, mutation: RemotePartMutation) {
        return this.request(documentId, 'update', mutation)
    }

    private request(documentId: string, action: Action, mutation: {
        requestId: string;
        part?: RemotePart;
        prepend?: boolean
    }) {
        const fingerprint = JSON.stringify({documentId, action, ...mutation})
        const existing = this.requests.get(mutation.requestId)
        if (existing) return existing.fingerprint === fingerprint ? existing.promise
            : Promise.reject(new RemotePartsFailure('Request ID reused with different parameters.', 'rejected', 'REQUEST_CONFLICT'))
        if (!this.connected) return Promise.reject(new RemotePartsFailure('WebSocket disconnected. Reconnect and retry. Your draft is retained.', 'rejected', 'DISCONNECTED'))
        let resolve!: Pending['resolve'], reject!: Pending['reject']
        const promise = new Promise<RemotePart[]>((success, failure) => {
            resolve = success;
            reject = failure
        })
        const pending: Pending = {
            documentId, action, fingerprint, promise, resolve, reject,
            timer: setTimeout(() => this.fail({
                documentId, requestId: mutation.requestId, action, code: 'OUTCOME_UNKNOWN', outcome: 'unknown',
                message: 'Confirmation timed out. Reload or retry the same request. Your draft is retained.'
            }), 35000),
        }
        this.requests.set(mutation.requestId, pending)
        if (isLoad(action)) store.dispatch(remotePartsLoading({documentId, requestId: mutation.requestId}))
        else store.dispatch(remotePartsMutationStarted({documentId, requestId: mutation.requestId, action}))
        const sent = messagesService.sendNow(`remote-document-parts.${action}`, {documentId, ...mutation})
        if (!sent) this.fail({
            documentId,
            requestId: mutation.requestId,
            action,
            code: 'DISCONNECTED',
            outcome: 'rejected',
            message: 'WebSocket is not ready. Retry when connected.'
        })
        return promise
    }

    receive(result: RemotePartsResult) {
        const request = this.requests.get(result.requestId)
        if (!request || request.documentId !== result.documentId || request.action !== result.action) return
        clearTimeout(request.timer)
        this.requests.delete(result.requestId)
        if (isLoad(request.action)) this.recoveryLoads.delete(result.documentId)
        store.dispatch(remotePartsReceived(result))
        if (!isLoad(request.action)) store.dispatch(remotePartsMutationFinished(result))
        request.resolve(result.parts)
    }

    changed(snapshot: RemotePartsSnapshot) {
        if (this.connected && this.subscriptions.has(snapshot.documentId)) store.dispatch(remotePartsReceived(snapshot))
    }

    fail(error: RemotePartsError) {
        const request = this.requests.get(error.requestId)
        if (!request || request.documentId !== error.documentId) return
        clearTimeout(request.timer)
        this.recoveryLoads.add(error.documentId)
        this.requests.delete(error.requestId)
        if (isLoad(request.action)) store.dispatch(remotePartsLoadFailed({...error}))
        else store.dispatch(remotePartsMutationFinished({...error, unknown: error.outcome === 'unknown'}))
        request.reject(new RemotePartsFailure(error.message, error.outcome, error.code))
    }

    private forget(documentId: string) {
        this.subscriptions.delete(documentId)
        if (this.connected) messagesService.sendNow('remote-document-parts.unsubscribe', {documentId})
        for (const [requestId, request] of [...this.requests]) {
            if (request.documentId === documentId) this.fail({
                documentId, requestId, action: request.action,
                code: 'OUTCOME_UNKNOWN', outcome: 'unknown', message: 'Document closed before confirmation.'
            })
        }
        this.recoveryLoads.delete(documentId)
        store.dispatch(remotePartsForgotten(documentId))
    }

    clear() {
        for (const documentId of [...this.subscriptions.keys()]) this.forget(documentId)
        // A header can submit without an active parts panel.
        for (const [requestId, request] of [...this.requests]) this.fail({
            documentId: request.documentId,
            requestId,
            action: request.action,
            code: 'OUTCOME_UNKNOWN',
            outcome: 'unknown',
            message: 'Session ended before confirmation.'
        })
        this.connected = false
        this.recoveryLoads.clear()
        store.dispatch(remotePartsCleared())
    }
}

export const remoteDocumentPartsService = new RemoteDocumentPartsService()
